import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import useSpeechRecognition from './useSpeechRecognition';
import { getSelectedLanguage } from '../../selectors/locale';

const useDictatedInput = ({ value, onChange, onTranscript }) => {
    const lang = useSelector(getSelectedLanguage);
    const usedVoiceRef = useRef(false);
    const valueRef = useRef(value);
    valueRef.current = value;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const onTranscriptRef = useRef(onTranscript);
    onTranscriptRef.current = onTranscript;

    const { isListening, isSupported, toggle, stop, syncBaseText } = useSpeechRecognition({
        onTranscript: next => {
            usedVoiceRef.current = true;
            onTranscriptRef.current?.(next);
            onChangeRef.current(next);
        },
        lang,
    });

    useEffect(() => {
        if (!value) usedVoiceRef.current = false;
    }, [value]);

    const toggleDictation = useCallback(() => toggle(valueRef.current), [toggle]);

    const onManualChange = useCallback(
        next => {
            if (!next) usedVoiceRef.current = false;
            onChangeRef.current(next);
            syncBaseText(next);
        },
        [syncBaseText]
    );

    const finishDictation = useCallback(() => {
        stop();
        return usedVoiceRef.current;
    }, [stop]);

    const markVoiceUnused = useCallback(() => {
        usedVoiceRef.current = false;
    }, []);

    return {
        isListening,
        isSupported,
        toggleDictation,
        stopDictation: stop,
        onManualChange,
        finishDictation,
        markVoiceUnused,
    };
};

export default useDictatedInput;
